import * as React from 'react'
import Button from './Button'
import styles from './ImageUpload.module.css'
import OptimizedImage from './OptimizedImage'
import Banner from './Banner'

interface ImageUploadPage {
  setPreview: (preview: string) => void,
  setBuffer: (buffer: Blob) => void,
  modalIsActive: boolean,
  setModalIsActive: (isActive: boolean) => void,
  imgSrc: string,
  setImgSrc: (imgSrc: string) => void,
  setImageFileName: (fileName: string) => void,
}

function ImageUpload({
  setPreview,
  setBuffer,
  modalIsActive,
  setModalIsActive,
  imgSrc,
  setImgSrc,
  setImageFileName,
}: ImageUploadPage) {

  const imgRef = React.useRef<HTMLImageElement | null>(null)
  const [aspectRatio, setAspectRatio] = React.useState<number>(1)
  const [fileSizeWarning, setFileSizeWarning] = React.useState<boolean>(false)
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  
  const MAX_FILE_SIZE = 30000000 // 30 MB

  function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      const files = e.target.files
      const file = files[0]
      
      setSelectedFile(file)

      if (file.size > MAX_FILE_SIZE) {
        setFileSizeWarning(true)
        setImgSrc('')
        return
      } else {
        setFileSizeWarning(false)
      }
      
      const reader = new FileReader()
      reader.addEventListener('load', () => {
        const fileName = file?.name || 'unknown.png'
        setImageFileName(fileName)
        setImgSrc(reader.result?.toString() || '')
      })
      reader.readAsDataURL(file)
    }
  }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { naturalWidth, naturalHeight } = e.currentTarget
    setAspectRatio(naturalWidth / naturalHeight)
  }

  function onUpload() {
    if (imgRef.current && selectedFile) {
      setPreview(imgRef.current.src)
      setModalIsActive(true)
      setBuffer(selectedFile)
    }
  }

  return (
    <div>
      {fileSizeWarning && (
        <div style={{ marginBottom: '1rem' }}>
          <Banner theme="warning">
            <p>Filen är för stor. Maximal filstorlek är 30 MB. Vänligen välj en mindre bild.</p>
          </Banner>
        </div>
      )}
      {imgSrc ? (
        <>
          <div className={styles.imageContainer}>
            <OptimizedImage
              ref={imgRef}
              className={styles.image}
              src={imgSrc}
              alt="Crop"
              sizes='50vw'
              onLoad={onImageLoad}
              aspectRatio={aspectRatio}
            />
          </div>
          <div className={styles.imageAction}>
            <Button size='large' className={styles.uploadButton} onClick={onUpload} disabled={modalIsActive || fileSizeWarning}>Ladda upp bild</Button>
            <div className={styles.newFileContainer}>
              <label htmlFor="fileUpload" className={styles.chooseNewFileButton}>
                Välj en ny bild
                <input id="fileUpload" type="file" className={styles.uploadInput} onChange={onSelectFile} />
              </label>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.uploadContainer}>
          <input
            className={styles.uploadInput}
            type="file"
            onChange={onSelectFile}
          />
          <div className={styles.selectText}>
            <p className={styles.selectLabel}>Släpp fil för att ladda upp</p>
            <span className={styles.selectRow}>eller</span>
            <span className={styles.selectButton}>Välj bild från fil</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default ImageUpload